import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GovItem, GovKind, getItems, governanceErrorMessage } from "../../../../utils/icGovernance";
import { statusesFor } from "../governanceStatus";

/**
 * Loads one register page by page. The filters live in the URL
 * (`?status=A,B&q=…&sort=…`, written with `replace`) so coming back from an
 * item restores them; `release` is the UAT register's `?release=` filter.
 */

export const DEFAULT_SORT = "-createdAt";
/** The server cuts the search to this many characters. */
export const MAX_Q = 100;

/** The sorts IC_Ticket supports (it cannot sort by due date). */
export const SORT_FIELDS: { field: string; label: string }[] = [
  { field: "createdAt", label: "Created" },
  { field: "updatedAt", label: "Updated" },
  { field: "priority", label: "Priority" },
  { field: "status", label: "Status" },
  { field: "title", label: "Title" },
];

const VALID_SORTS = new Set(SORT_FIELDS.flatMap((f) => [f.field, `-${f.field}`]));

export interface RegisterQuery {
  /** Stored statuses; empty = every status. */
  status: string[];
  q: string;
  sort: string;
}

export interface RegisterState<T extends GovItem> {
  rows: T[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  nextCursor: string | null;
  onLoadMore: () => void;
  query: RegisterQuery;
  onQueryChange: (patch: Partial<RegisterQuery>) => void;
}

export const useRegisterItems = <T extends GovItem>(
  kind: GovKind,
  opts: { release?: string | null } = {},
): RegisterState<T> => {
  const [searchParams, setSearchParams] = useSearchParams();
  const release = opts.release || undefined;

  const statusParam = useMemo(() => {
    const allowed = new Set(statusesFor(kind));
    return (searchParams.get("status") ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => allowed.has(s))
      .join(",");
  }, [searchParams, kind]);
  const q = (searchParams.get("q") ?? "").trim().slice(0, MAX_Q).trim();
  const rawSort = searchParams.get("sort") ?? "";
  const sort = VALID_SORTS.has(rawSort) ? rawSort : DEFAULT_SORT;

  const query = useMemo<RegisterQuery>(
    () => ({ status: statusParam ? statusParam.split(",") : [], q, sort }),
    [statusParam, q, sort],
  );

  const [rows, setRows] = useState<T[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A slow earlier response must never overwrite a newer filter's results.
  const requestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setLoadingMore(false);
    setError(null);
    getItems({ kind, status: statusParam || undefined, q: q || undefined, sort, release })
      .then((page) => {
        if (requestId !== requestIdRef.current) return;
        setRows((page.items ?? []) as T[]);
        setNextCursor(page.nextCursor ?? null);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        console.error(`Failed to load ${kind} register:`, err);
        setRows([]);
        setNextCursor(null);
        setError(governanceErrorMessage(err));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, [kind, statusParam, q, sort, release]);

  const onLoadMore = useCallback(() => {
    if (!nextCursor || loadingMore) return;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    getItems({ kind, status: statusParam || undefined, q: q || undefined, sort, release, cursor: nextCursor })
      .then((page) => {
        if (requestId !== requestIdRef.current) return;
        setRows((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...((page.items ?? []) as T[]).filter((r) => !seen.has(r.id))];
        });
        setNextCursor(page.nextCursor ?? null);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        setError(governanceErrorMessage(err));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoadingMore(false);
      });
  }, [kind, statusParam, q, sort, release, nextCursor, loadingMore]);

  const onQueryChange = useCallback(
    (patch: Partial<RegisterQuery>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const set = (key: string, value: string, empty: string) => {
            if (!value || value === empty) params.delete(key);
            else params.set(key, value);
          };
          if (patch.status !== undefined) set("status", patch.status.join(","), "");
          if (patch.q !== undefined) set("q", patch.q.trim().slice(0, MAX_Q).trim(), "");
          if (patch.sort !== undefined) set("sort", patch.sort, DEFAULT_SORT);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { rows, loading, loadingMore, error, nextCursor, onLoadMore, query, onQueryChange };
};
